import cloneDeep from 'lodash/cloneDeep';

import { environment } from '@env/environment';
import { Vragenboom, Controltype, Antwoord, Vraag, VragenboomMetadata, Vraagtype } from '@models/claims';
import { SchadeMeldenError } from '@models/vragenboom';
import { VraagHelper } from '../helpers/vraag-helper';
import { ValidationMessage } from '../models/validation-message';

export class VragenboomValidator {
  static validate(vragenboom: Vragenboom) {
    

    const vragenboomToVerify = cloneDeep(vragenboom);
    const messages: ValidationMessage[] = [];

    if (vragenboomToVerify == null || vragenboom.vragen == null) {
      this.addMessage('Vragenboom contains no questions.', messages);
    } else {
      // 1.  Validate metadata
      this.validateMetadata(vragenboomToVerify.vragenboomMetadata, messages);

      // 2.  Vragenboom bevat 1 startvraag
      this.validateOnlyOneStartVraag(vragenboomToVerify, messages);

      // 3.  Iedere vraag (op startvraag na) wordt gelinkt via minimaal 1 antwoord
      this.eachQuestionIsLinkedByAtLeastOneAnswer(vragenboomToVerify, messages);

      // 4.  Iedere vraagReeks in de vragenboom moet eindigen met een eindvraag
      // 5.  Iedere linkt_naar refereert naar vraag in vragenboom
      // 6.  Een vragenreeks mag geen loops bevatten
      this.eachVragenReeksShouldResultInEindvraag(vragenboomToVerify, messages);

      // 7.  Groep_id moet oplopend zijn
      this.validateGroups(vragenboomToVerify, messages);

      // 8.  Iedere vraag + antwoord moet bepaalde attributen bevatten
      this.validateQuestionAndAnswerAttributes(vragenboomToVerify, messages);
    }

    if (messages.length > 0 && window.location?.hostname && 
        !window.location.hostname.startsWith('www.interpolis.nl') &&
        !window.location.hostname.startsWith('stg.www.interpolis.nl')) {      
      for (const message of messages) {
        console.log(message.type + ': ' + message.message);
      }      
    }
    

    var errors = messages.filter(m => m.type === 'Error');
    if (errors.length > 0) {
      console.error('Vragenboom is invalid');

      let key = 'unknown';
      if (vragenboomToVerify && vragenboomToVerify.vragenboomMetadata) {
        key = vragenboomToVerify.vragenboomMetadata.sleutel;
      }
      if (environment.production) {
        throw new SchadeMeldenError(`ols-invalid-vragenboom-${key}`, 'Vragenboom is invalid');
      }
    }
  }

  private static validateMetadata(metadata: VragenboomMetadata, messages: ValidationMessage[]) {
    if (!metadata) {
      this.addMessage('Vragenboom does not contain metadata', messages);
      return;
    }

    if (metadata.vervallen) {
      this.addMessage('Metadata of vragenboom is invalid: vervallen is true', messages);
    }

    if (!metadata.versienr) {
      this.addMessage('Metadata of vragenboom is invalid: versienr is empty', messages);
    }

    if (!metadata.label || metadata.label !== 'IPO') {
      this.addMessage('Metadata of vragenboom is invalid: label is invalid', messages);
    }

    if (!metadata.kanaal || metadata.kanaal !== 'WEB') {
      this.addMessage('Metadata of vragenboom is invalid: kanaal is invalid', messages);
    }
  }

  private static validateOnlyOneStartVraag(vragenboom: Vragenboom, messages: ValidationMessage[]) {
    if (vragenboom == null) {
      return;
    }
    if (vragenboom.vragen.length === 0 && vragenboom.vragenboomMetadata.sleutel === 'BIJWERKENCLAIMRESPONSE') {
      return;
    }

    const startVragen: Vraag[] = vragenboom.vragen.filter(v => v.startvraag);
    if (startVragen.length === 0) {
      this.addMessage('Vraagboom does not contain startvraag', messages);
    }

    if (startVragen.length > 1) {
      this.addMessage('Vragenboom contains multiple startvragen. Namely: ' + startVragen.map(v => v.id).join(', '), messages);
    }
  }

  private static eachQuestionIsLinkedByAtLeastOneAnswer(vragenboom: Vragenboom, messages: ValidationMessage[]) {
    if (vragenboom == null || vragenboom.vragen.length === 0) {
      return;
    }

    // Get all linktnaar ids
    const linktNaarIds: string[] = [];
    for (const vraag of vragenboom.vragen) {
      if (vraag.linkt_naar) {
        const conditions = vraag.conditions ?? [];
        for (const condition of conditions) {
          if (linktNaarIds.indexOf(condition.linkt_naar) < 0) {
            linktNaarIds.push(condition.linkt_naar);
          }
        }
      } else {
        if (vraag.antwoorden) {
          for (const antwoord of vraag.antwoorden) {
            if (antwoord.linkt_naar) {
              if (linktNaarIds.indexOf(antwoord.linkt_naar) < 0) {
                linktNaarIds.push(antwoord.linkt_naar);
              }
            }
          }
        }
      }
    }

    // Retrieve vragen that are not startvragen and are not linked by answer
    const vragen = vragenboom.vragen.filter(v => !v.startvraag && linktNaarIds.indexOf(v.id) < 0);

    if (vragen.length > 0) {
      this.addMessage('Vragenboom contains vragen that are not connected by answer. Namely: ' + vragen.map(v => v.id).join(', '), messages);
    }
  }

  private static validateGroups(vragenboom: Vragenboom, messages: ValidationMessage[]) {
    if (vragenboom == null || vragenboom.vragen.length === 0) {
      return;
    }

    for (const vraag of vragenboom.vragen) {
      if (vraag.linkt_naar) {
        var nextQuestion = vragenboom.vragen.find(v => v.id === vraag.linkt_naar);
        this.validateIncreasingGroupValuesWithNextQuestion(vraag, nextQuestion, messages);

      } else if (vraag.antwoorden && vraag.antwoorden.length > 0) {
        for (const antwoord of vraag.antwoorden) {
          if (antwoord.linkt_naar) {
            var nextQuestion = vragenboom.vragen.find(v => v.id === antwoord.linkt_naar);
            this.validateIncreasingGroupValuesWithNextQuestion(vraag, nextQuestion, messages);
          }
        }
      }
    }
  }
  
  private static validateIncreasingGroupValuesWithNextQuestion(currentVraag: Vraag, nextVraag: Vraag, messages: ValidationMessage[]) {
    if (currentVraag == null || nextVraag == null) {
      return;
    }

    if (Number(nextVraag.groep_id) < Number(currentVraag.groep_id)) {
      this.addMessage('The vraag with id: ' + currentVraag.id + ' is followed by question with id: ' + nextVraag.id + ' and does not have increasing group ids.', messages, 'Warning');
    } else if (Number(currentVraag.groep_id) === Number(nextVraag.groep_id) && currentVraag.groep_titel !== nextVraag.groep_titel) {
      // Als groep_id gelijk dan zou groep_titel gelijk moeten zijn
      this.addMessage('The vraag with id: ' + currentVraag.id + ' is followed by question with id: ' + nextVraag.id + ' with same groep_id (' + currentVraag.groep_id +'), but different groep_titel: ' + currentVraag.groep_titel + ', ' + nextVraag.groep_titel + '.', messages, 'Warning');
    } else if (Number(currentVraag.groep_id) === (Number(nextVraag.groep_id) + 1) && currentVraag.groep_titel === nextVraag.groep_titel && nextVraag.groep_titel != '') {
      // Als groep_id ongelijk aan vorige, dan zou groep_titel niet gelijk moeten zijn
      this.addMessage('The vraag with id: ' + currentVraag.id + ' is followed by question with id: ' + nextVraag.id + ' have different groep_ids, but have same groep_titel.', messages, 'Warning');
    }
  }

  private static eachVragenReeksShouldResultInEindvraag(vragenboom: Vragenboom, messages: ValidationMessage[]) {
    if (vragenboom == null || vragenboom.vragen.length === 0) {
      return;
    }

    const startvraag = vragenboom.vragen.find(v => v.startvraag);
    const reeks: Vraag[] = [];
    reeks.push(startvraag);

    this.vragenReeksHasEndQuestion(vragenboom, messages, reeks, []);
  }

  private static vragenReeksHasEndQuestion(vragenboom: Vragenboom, messages: ValidationMessage[], reeks: Vraag[], verifiedNodes: string[]) {
    const currentvraag = reeks[reeks.length - 1];
    if (VraagHelper.isEindvraag(currentvraag)) {
      return;
    }

    if (VraagHelper.isEindvraag(currentvraag) === false && (currentvraag.linkt_naar == null && (currentvraag.antwoorden == null ||
      currentvraag.antwoorden.length === 0))) {
      this.addMessage('The vragenreeks with ids: ' + reeks.map(v => v.id).join(', ') + ' does not ends with end question.', messages);
      return;
    }

    if (currentvraag.linkt_naar != null) {
      const conditions = currentvraag.conditions ?? [];
      for (const condition of conditions) {
        this.verifyNewReeks(vragenboom, messages, reeks, verifiedNodes, condition.linkt_naar, currentvraag.groep_id, currentvraag.groep_titel);
      }
    } else {
      if (currentvraag.antwoorden != null) {
        for (const antwoord of currentvraag.antwoorden) {
          if (antwoord.linkt_naar) {
            this.verifyNewReeks(vragenboom, messages, reeks, verifiedNodes, antwoord.linkt_naar, currentvraag.groep_id, currentvraag.groep_titel);
          }
        }
      }
    }
  }

  private static verifyNewReeks(vragenboom: Vragenboom, messages: ValidationMessage[], reeks: Vraag[], verifiedNodes: string[], newVraagId: string, groepId: string, groepTitel: string) {
    if (newVraagId == null) {
      this.addMessage('The vragenreeks ' + reeks.map(v => v.id).join(', ') + ' contains an empty linkt_naar', messages);
      return;
    }

    if (reeks.findIndex(v => v.id === newVraagId) >= 0) {
      this.addMessage('The question ' + newVraagId + ' is already added to vragenreeks ' + reeks.map(v => v.id).join(', '), messages);
      return;
    }

    if (verifiedNodes.indexOf(newVraagId) >= 0) {
      // Vraag is already verified
      return;
    }
    verifiedNodes.push(newVraagId);

    const newVraag = this.retrieveVraag(vragenboom, newVraagId);
    if (newVraag == null) {
      this.addMessage('The vraag ' + reeks[reeks.length - 1].id + ' contains linkt_naar ' + newVraagId +
        ' which is not added as question in vraagboom.', messages);
      return;
    }

    const newReeks = cloneDeep(reeks);
    newReeks.push(newVraag);
    this.vragenReeksHasEndQuestion(vragenboom, messages, newReeks, verifiedNodes);
  }

  private static validateQuestionAndAnswerAttributes(vragenboom: Vragenboom, messages: ValidationMessage[]) {
    if (vragenboom == null || vragenboom.vragen.length === 0) {
      return;
    }

    for (const vraag of vragenboom.vragen) {
      this.validateQuestion(vraag, messages);
      this.validateVraagConditions(vraag, vragenboom, messages);

      if (vraag.antwoorden) {
        for (const antwoord of vraag.antwoorden) {
          this.validateAnswer(vraag, antwoord, messages);
        }
      }
    }
  }

  private static validateQuestion(vraag: Vraag, messages: ValidationMessage[]) {
    if (vraag == null) {
      return;
    }

    if (!vraag.id) {
      this.addMessage('Vragenboom contains question without id', messages);
    }

    if (!vraag.titel && vraag.tonen && vraag.control !== Controltype.Label && !(vraag.control === Controltype.SuccesVerder && vraag.vraagtype === Vraagtype.Versturen)) {
      this.addMessage('The vraag ' + vraag.id + ' does not contain a title', messages);
    }

    if (!vraag.groep_id) {
      this.addMessage('The vraag ' + vraag.id + ' does not contain a groep id', messages);
    }

    if (vraag.control == null || Object.keys(Controltype).map(e => Controltype[e]).indexOf(vraag.control) < 0) {
      this.addMessage('The vraag ' + vraag.id + ' does not contain a valid control type', messages);
    }

    if (vraag.control !== Controltype.Label && vraag.control !== Controltype.Failure && (vraag.antwoorden == null || vraag.antwoorden.length <= 0)) {
      this.addMessage('The vraag ' + vraag.id + ' does not contain any answers', messages);
    }

    if (!vraag.tonen) {
      if (vraag.antwoorden != null && vraag.antwoorden.filter(a => a.standaard_waarde === 'True').length !== 1) {
        this.addMessage('The hidden vraag ' + vraag.id + ' does not contain one standaard waarde answer', messages);
      }
      if (![Controltype.SingleChoice, Controltype.Summary, Controltype.Label, Controltype.Button, Controltype.SingleChoiceRepair, Controltype.SingleChoiceRepairWithSearch].includes(vraag?.control)) {
        this.addMessage('The hidden vraag ' + vraag.id + ' is not of type SingleChoice or Label or Button', messages);
      }
    }

    if (vraag.control === Controltype.Tekst && !vraag.linkt_naar && vraag.antwoorden.length > 1) {
      this.addMessage('The tekst vraag ' + vraag.id + ' with multiple answers does not contain linkt_naar', messages);
    }
  }

  private static validateAnswer(vraag: Vraag, antwoord: Antwoord, messages: ValidationMessage[]) {
    if (vraag == null || antwoord == null) {
      return;
    }

    if (!antwoord.id) {
      this.addMessage('The question ' + vraag.id + ' contains answer without id', messages);
    }

    if (!vraag.conditions || vraag.conditions.length == 0) {
      if (vraag.control === Controltype.Tekst) {
        if (vraag.antwoorden.length === 1 && !antwoord.linkt_naar && !vraag.linkt_naar) {
          this.addMessage('The answer ' + antwoord.id + ' of vraag ' + vraag.id + ' does not contain linkt_naar', messages);
        }
      } else if (!antwoord.linkt_naar && !VraagHelper.isEindvraag(vraag) && vraag.tonen
        && vraag.control !== Controltype.ContactGegevens && vraag.control !== Controltype.MultiChoice && vraag.control !== Controltype.Blob
        && vraag.control !== Controltype.Telefoon1 && vraag.control !== Controltype.Telefoon2 && vraag.control !== Controltype.Telefoon3) {
        this.addMessage('The answer ' + antwoord.id + ' of vraag ' + vraag.id + ' does not contain linkt_naar', messages);
      }
    }

    if ((vraag.control === Controltype.Kalender || vraag.control === Controltype.KalenderDmj
      || vraag.control === Controltype.ContactGegevens || vraag.control === Controltype.Tekst) &&
      !antwoord.variabeleWaarde) {
      this.addMessage('The answer ' + antwoord.id + ' of vraag ' + vraag.id + ' does not contain variable waarde', messages);
    }

    if ((vraag.control === Controltype.SingleChoice || vraag.control === Controltype.MultiChoice) && !antwoord.titel) {
      this.addMessage('The answer ' + antwoord.id + ' of vraag ' + vraag.id + ' does not contain a titel', messages);
    }
  }

  private static validateVraagConditions(vraag: Vraag, vragenboom: Vragenboom, messages: ValidationMessage[]) {
    if (vraag == null || vragenboom == null || !vraag.conditions || vraag.conditions.length == 0) {
      return;
    }

    const vragenboomIds: string[] = vragenboom.vragen.map(vraag => vraag.id);
    vraag.conditions.forEach(condition => {
      if (vragenboomIds.indexOf(condition.linkt_naar) < 0)
        this.addMessage(`The vraag '${vraag.id}' contains a 'linkt_naar' condition with an invalid output vraagId '${condition.linkt_naar}'`, messages);

      condition.antwoorden?.forEach(va => {
        const inputVraag = vragenboom.vragen.find(vr => vr.id === va.vraagId);
        if (!inputVraag)
          this.addMessage(`The vraag '${vraag.id}' contains a 'linkt_naar' condition with an invalid input vraagId '${va.vraagId}'`, messages);
        else if (!va.antwoordId.startsWith('{') && inputVraag.antwoorden.map(x => x.id).indexOf(va.antwoordId) < 0)
          this.addMessage(`The vraag '${vraag.id}' contains a 'linkt_naar' condition with an invalid input antwoordId '${va.antwoordId}' for input vraagId '${va.vraagId}'`, messages);
      });
    });
  }

  private static retrieveVraag(vragenboom: Vragenboom, id: string): Vraag {
    if (vragenboom == null) {
      return null;
    }

    return vragenboom.vragen.find(v => v.id === id);
  }

  private static addMessage(message: string, messages: ValidationMessage[], type: string = 'Error') {
    if (messages == null) {
      return;
    }

    if (!messages.find(m => m.message === message && m.type === type)) {
      messages.push(new ValidationMessage (message, type));
    }
  }
}
